import React, { useState } from 'react';
import { useStocks } from '../hooks';
import { Stock } from '../types';
import { apiService } from '../services/api';
import { formatLondonDateTime } from '../shared/dateUtils';
import StockForm from './StockForm';
import Modal from './Modal';
import './StockManagement.css';

const StockManagement: React.FC = () => {
    const { data: stocks, loading, error, refetch } = useStocks();
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingStock, setEditingStock] = useState<Stock | undefined>(undefined);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [showInactive, setShowInactive] = useState(false);
    const [nameFilter, setNameFilter] = useState('');

    const handleCreate = () => {
        setEditingStock(undefined);
        setIsFormOpen(true);
        setSubmitError(null);
    };

    const handleEdit = (stock: Stock) => {
        setEditingStock(stock);
        setIsFormOpen(true);
        setSubmitError(null);
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this stock item? This action cannot be undone.')) {
            return;
        }

        try {
            await apiService.deleteStock(id);
            setSuccessMessage('Stock item deleted successfully!');
            setTimeout(() => setSuccessMessage(null), 3000);
            refetch();
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to delete stock item');
        }
    };

    const handleSubmit = async (stockData: Omit<Stock, 'id' | 'updatedAt'>) => {
        try {
            setIsSubmitting(true);
            setSubmitError(null);

            if (editingStock) {
                await apiService.updateStock(editingStock.id, stockData);
                setSuccessMessage('Stock item updated successfully!');
            } else {
                await apiService.createStock(stockData);
                setSuccessMessage('Stock item created successfully!');
            }

            setTimeout(() => setSuccessMessage(null), 3000);
            setIsFormOpen(false);
            setEditingStock(undefined);
            refetch();
        } catch (err) {
            setSubmitError(err instanceof Error ? err.message : 'Failed to save stock item');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        setIsFormOpen(false);
        setEditingStock(undefined);
        setSubmitError(null);
    };

    const handleMoveUp = async (stock: Stock, currentIndex: number) => {
        if (currentIndex === 0) return;

        try {
            // Create a new array with swapped positions
            const reordered = [...filteredStocks];
            reordered.splice(currentIndex, 1);
            reordered.splice(currentIndex - 1, 0, stock);

            // Reassign sortOrder to all items: 10, 20, 30, etc.
            const updates = reordered.map((item, index) =>
                apiService.updateStock(item.id, { sortOrder: (index + 1) * 10 })
            );

            await Promise.all(updates);
            refetch();
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to reorder stock items');
        }
    };

    const handleMoveDown = async (stock: Stock, currentIndex: number) => {
        if (currentIndex === filteredStocks.length - 1) return;

        try {
            // Create a new array with swapped positions
            const reordered = [...filteredStocks];
            reordered.splice(currentIndex, 1);
            reordered.splice(currentIndex + 1, 0, stock);

            // Reassign sortOrder to all items: 10, 20, 30, etc.
            const updates = reordered.map((item, index) =>
                apiService.updateStock(item.id, { sortOrder: (index + 1) * 10 })
            );

            await Promise.all(updates);
            refetch();
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to reorder stock items');
        }
    };

    const formatDate = (dateString: string) => {
        return formatLondonDateTime(dateString);
    };

    // Filter and sort stocks
    const filteredStocks = React.useMemo(() => {
        if (!stocks) return [];

        let filtered = stocks;

        // Filter by isActive (if isActive field doesn't exist, treat as inactive)
        if (!showInactive) {
            filtered = filtered.filter(stock => stock.isActive === true);
        }

        // Filter by name
        if (nameFilter.trim()) {
            const searchTerm = nameFilter.toLowerCase();
            filtered = filtered.filter(stock =>
                stock.name.toLowerCase().includes(searchTerm)
            );
        }

        // Sort by sortOrder (if exists), then by name
        return [...filtered].sort((a, b) => {
            const aOrder = a.sortOrder ?? 999999;
            const bOrder = b.sortOrder ?? 999999;
            if (aOrder !== bOrder) {
                return aOrder - bOrder;
            }
            return a.name.localeCompare(b.name);
        });
    }, [stocks, showInactive, nameFilter]);

    if (loading) {
        return (
            <div className="stock-management">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading stock items...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="stock-management">
                <div className="error-container">
                    <h2>Error Loading Stock</h2>
                    <p>{error}</p>
                    <button onClick={refetch} className="button primary">
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="stock-management">
            <div className="stock-header">
                <div>
                    <h1>📦 Stock Management</h1>
                    <p className="subtitle">Manage your ingredient and material inventory</p>
                </div>
                <button onClick={handleCreate} className="button primary">
                    ➕ Add Stock Item
                </button>
            </div>

            {stocks && stocks.length > 0 && (
                <div className="stock-filters">
                    <div className="filter-group">
                        <input
                            type="text"
                            placeholder="Search by name..."
                            value={nameFilter}
                            onChange={(e) => setNameFilter(e.target.value)}
                            className="search-input"
                        />
                    </div>
                    <div className="filter-group">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={showInactive}
                                onChange={(e) => setShowInactive(e.target.checked)}
                            />
                            <span>Show Inactive Items</span>
                        </label>
                    </div>
                </div>
            )}

            {successMessage && (
                <div className="success-message">
                    <span className="success-icon">✅</span>
                    {successMessage}
                </div>
            )}

            {!stocks || stocks.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">📦</div>
                    <h3>No Stock Items Yet</h3>
                    <p>Create your first stock item to start tracking inventory</p>
                    <button onClick={handleCreate} className="button primary">
                        ➕ Add Stock Item
                    </button>
                </div>
            ) : filteredStocks.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">🔍</div>
                    <h3>No Stock Items Found</h3>
                    <p>Try adjusting your filters or create a new stock item</p>
                </div>
            ) : (
                <div className="stock-table-container">
                    <table className="stock-table">
                        <thead>
                            <tr>
                                <th>Order</th>
                                <th>Name</th>
                                <th>Status</th>
                                <th>Quantity</th>
                                <th>Unit</th>
                                <th>Allergens</th>
                                <th>Notes</th>
                                <th>Last Updated</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredStocks.map((stock, index) => (
                                <tr key={stock.id}>
                                    <td className="stock-order">
                                        <div className="order-controls">
                                            <button
                                                onClick={() => handleMoveUp(stock, index)}
                                                className="order-button"
                                                disabled={index === 0}
                                                title="Move up"
                                            >
                                                ▲
                                            </button>
                                            <span className="order-number">{stock.sortOrder ?? '-'}</span>
                                            <button
                                                onClick={() => handleMoveDown(stock, index)}
                                                className="order-button"
                                                disabled={index === filteredStocks.length - 1}
                                                title="Move down"
                                            >
                                                ▼
                                            </button>
                                        </div>
                                    </td>
                                    <td className="stock-name">
                                        <strong>{stock.name}</strong>
                                    </td>
                                    <td className="stock-status">
                                        <span className={`status-badge ${stock.isActive ? 'active' : 'inactive'}`}>
                                            {stock.isActive ? '✓ Active' : '✗ Inactive'}
                                        </span>
                                    </td>
                                    <td className="stock-quantity">
                                        {stock.quantity}
                                    </td>
                                    <td>{stock.unit || '-'}</td>
                                    <td>
                                        {stock.allergens && stock.allergens.length > 0 ? (
                                            <div className="allergen-badges">
                                                {stock.allergens.slice(0, 3).map((allergen, idx) => (
                                                    <span key={idx} className="allergen-badge">
                                                        {allergen.allergen}
                                                    </span>
                                                ))}
                                                {stock.allergens.length > 3 && (
                                                    <span className="allergen-badge more">
                                                        +{stock.allergens.length - 3}
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-muted">None</span>
                                        )}
                                    </td>
                                    <td className="stock-notes">
                                        {stock.notes ? (
                                            <span className="notes-text">{stock.notes}</span>
                                        ) : (
                                            <span className="text-muted">-</span>
                                        )}
                                    </td>
                                    <td className="stock-date">
                                        {formatDate(stock.updatedAt)}
                                    </td>
                                    <td className="stock-actions">
                                        <button
                                            onClick={() => handleEdit(stock)}
                                            className="action-button edit"
                                            title="Edit"
                                        >
                                            ✏️
                                        </button>
                                        <button
                                            onClick={() => handleDelete(stock.id)}
                                            className="action-button delete"
                                            title="Delete"
                                        >
                                            🗑️
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Modal
                isOpen={isFormOpen}
                onClose={handleCancel}
                showCloseButton={true}
            >
                <StockForm
                    stock={editingStock}
                    onSubmit={handleSubmit}
                    onCancel={handleCancel}
                    isSubmitting={isSubmitting}
                />
                {submitError && (
                    <div className="error-message" style={{ marginTop: '1rem' }}>
                        <span className="error-icon">❌</span>
                        {submitError}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default StockManagement;
